package pl.decks.personal.pages.ievp

import androidx.compose.runtime.*
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.components.icons.fa.FaPaperPlane
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.serialization.ExperimentalSerializationApi
import org.jetbrains.compose.web.css.percent
import org.w3c.dom.*
import org.w3c.dom.events.Event
import org.w3c.fetch.RequestInit
import pl.decks.personal.components.layouts.site.MAIN_SCROLLING_AREA
import pl.decks.personal.components.layouts.site.SiteLayoutData
import pl.decks.personal.components.widgets.button.ButtonStylesheet
import pl.decks.personal.components.widgets.button.IconButton
import pl.decks.personal.components.widgets.mail.MailLink
import pl.decks.personal.components.widgets.pageprogress.PageProgressPointsBar
import pl.decks.personal.components.widgets.sitckybg.StickyBackground
import pl.decks.personal.pages.ievp.IEVPStylesheet.Content
import pl.decks.personal.pages.ievp.IEVPStylesheet.Section
import pl.decks.personal.pages.ievp.foreword.AtriaForeword
import pl.decks.personal.pages.ievp.foreword.BioptimusForeword
import pl.decks.personal.pages.ievp.foreword.Foreword
import pl.decks.personal.pages.ievp.foreword.ICEYEForeword
import pl.decks.personal.pages.ievp.foreword.MixRankForeword
import pl.decks.personal.pages.ievp.foreword.PostHogForeword
import pl.decks.personal.pages.ievp.foreword.TinyPharmaForeword
import pl.decks.personal.pages.ievp.foreword.TriggerDevForeword
import pl.decks.personal.pages.ievp.foreword.ZapierForeword
import pl.decks.personal.pages.ievp.sections.background.BackgroundSectionContent
import pl.decks.personal.pages.ievp.sections.experience.ExperienceSectionContent
import pl.decks.personal.pages.ievp.sections.insights.InsightsSectionContent
import pl.decks.personal.pages.ievp.sections.lastyears.LastYearsSectionContent
import pl.decks.personal.pages.ievp.sections.pitch.PitchSectionContent
import pl.decks.personal.pages.ievp.sections.profile.ProfileSectionContent
import pl.decks.personal.pages.ievp.sections.skills.SkillsSectionContent
import pl.decks.personal.pages.ievp.sections.why.IEVPWhySectionContent
import pl.decks.personal.site.BuildConfig
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout
import pl.decks.posthog.client.PostHogClient
import kotlin.js.json
import kotlin.math.pow
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

@InitRoute
fun initIEmployeeValuePropositionPage(ctx: InitRouteContext) {
    ctx.data.add(SiteLayoutData("Inverse Employee Value Proposition"))
}

@Page
@Layout(".components.layouts.site.SiteLayout")
@Composable
fun IEmployeeValuePropositionPage() {
    val pageContext = rememberPageContext()
    val controller = remember { IEVPPageController() }
    val state = controller.uiState
    val foreword = pageContext.route.queryParams["foreword"]?.let {
        Foreword.entries.find { e -> e.name == it.uppercase() }
    }

    LaunchedEffect(Unit) {
        PostHogClient.capture(IEVPJourney.JourneyStarted)
    }
    LaunchedEffect(foreword) {
        if (foreword != null) PostHogClient.capture(IEVPJourney.CustomForewordPresented(foreword))
    }

    DisposableEffect(state.scrollToSection) {
        state.scrollToSection?.let { section ->
            document.getElementById(section.toId())?.scrollIntoView(
                ScrollIntoViewOptions(
                    behavior = ScrollBehavior.SMOOTH,
                    block = ScrollLogicalPosition.START,
                )
            )
        }
        controller.handleEvent(IEVPPageEvent.ScrollToSectionConsumed)
        onDispose { }
    }
    Box(IEVPStylesheet.Page.toModifier(), contentAlignment = Alignment.TopStart) {
        state.background?.let { background ->
            StickyBackground {
                background(1f - state.incomingTransition)
            }
        }
        state.incomingBackground?.let { incoming ->
            StickyBackground {
                incoming(state.incomingTransition)
            }
        }

        IEVPSections(
            state,
            foreword,
            onMeasurementsChanged = { section, measures ->
                controller.handleEvent(IEVPPageEvent.SectionMeasuresChanged(section, measures))
            },
            navigateToSection = { section ->
                controller.handleEvent(IEVPPageEvent.SectionClicked(section))
            },
            onSectionReached = { section ->
                controller.handleEvent(IEVPPageEvent.SectionReached(section))
            },
            modifier = Modifier.zIndex(0)
        )
        PageProgressPointsBar(
            numberBreakpoints = IEVPSection.entries.size,
            activeBreakpoint = state.activeSection.ordinal,
            modifier = Modifier.zIndex(1),
            onPointClicked = {
                PostHogClient.capture(IEVPJourney.SideBarNavigated(IEVPSection.entries[it]))
                controller.handleEvent(IEVPPageEvent.SectionClicked(IEVPSection.entries[it]))
            }
        )
    }
}

@Composable
private fun IEVPSections(
    state: IEVPPageState,
    foreword: Foreword?,
    onMeasurementsChanged: (IEVPSection, IEVPPageState.SectionMeasures) -> Unit,
    navigateToSection: (IEVPSection) -> Unit,
    onSectionReached: (IEVPSection) -> Unit,
    modifier: Modifier = Modifier,
) = Column(
    Modifier.fillMaxWidth().then(modifier),
    horizontalAlignment = Alignment.CenterHorizontally,
) {
    foreword?.let { CompanyForeword(it) }

    IEVPSection.entries.forEach { section ->
        val headingProgress = state.headerProgress[section] ?: 1f
        val sectionProgress = state.sectionProgress[section] ?: 0f
        val sectionReached = section in state.sectionReached
        IEVPSectionContent(
            section = section,
            headingProgress = headingProgress,
            onSectionReached = { onSectionReached(section) },
            onMeasurementsChanged = { onMeasurementsChanged(section, it) },
        ) {
            when (section) {
                IEVPSection.WHY -> IEVPWhySectionContent(
                    sectionProgress,
                    onProceedClicked = {
                        PostHogClient.capture(IEVPJourney.ExplanationSkipped)
                        navigateToSection(IEVPSection.PITCH)
                    }
                )

                IEVPSection.PITCH -> PitchSectionContent()
                IEVPSection.KEY_INSIGHTS -> InsightsSectionContent(sectionProgress)
                IEVPSection.LAST_YEARS -> LastYearsSectionContent(sectionProgress)
                IEVPSection.PROFILE -> ProfileSectionContent(headingProgress)
                IEVPSection.SKILLS -> SkillsSectionContent(headingProgress)
                IEVPSection.BACKGROUND -> BackgroundSectionContent(sectionProgress)
                IEVPSection.EXPERIENCE -> ExperienceSectionContent(sectionReached)
            }
        }
    }

    PartingWords()
}

@Composable
private fun IEVPSectionContent(
    section: IEVPSection,
    headingProgress: Float,
    onSectionReached: () -> Unit,
    onMeasurementsChanged: (IEVPPageState.SectionMeasures) -> Unit,
    content: @Composable () -> Unit,
) {
    var isVisible by remember { mutableStateOf(false) }
    ParseMeasuresOnScroll(
        section = section,
        onResize = { isVisible = it.isIntersecting },
        onScroll = { if (isVisible) onMeasurementsChanged(it) }
    )
    LaunchedEffect(headingProgress) {
        if (headingProgress > 0.1f) onSectionReached()
    }

    Box(
        modifier = Section.Style.toModifier(
            if (section.background != null) Section.TransparentVariant else Section.CoveredVariant,
            if (section.invertColors(ColorMode.current)) Section.InvertedTextVariant else Section.NormalTextVariant
        ).id(section.toId()),
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(Content.Container.toModifier()) {
            SectionContentHeader(
                title = section.title,
                flavour = section.flavourQuote,
                modifier = Modifier
                    .setVariable(
                        IEVPStylesheet.Vars.contentHeaderOpacity,
                        headingProgress.pow(3).times(100).toInt().percent
                    )
            )
            content()
        }
    }
}

@Composable
private fun SectionContentHeader(
    title: String,
    flavour: String?,
    modifier: Modifier = Modifier,
) = Column(Content.Header.toModifier().then(modifier)) {
    SpanText(title, modifier = Content.Title.toModifier())
    if (flavour != null) SpanText("\"$flavour\"", modifier = Content.Flavour.toModifier())
}

@Composable
private fun ParseMeasuresOnScroll(
    section: IEVPSection,
    onResize: (IntersectionObserver.Entry) -> Unit,
    onScroll: (IEVPPageState.SectionMeasures) -> Unit,
) = DisposableEffect(section) {
    val element = document.getElementById(section.toId())
        ?: return@DisposableEffect onDispose {}
    val scrollingArea = document.getElementById(MAIN_SCROLLING_AREA)
        ?: return@DisposableEffect onDispose {}

    val observer = IntersectionObserver(
        options = IntersectionObserver.Options(thresholds = listOf(0.0)),
        resized = { entries, _ -> onResize(entries[0]) },
    )
    val scrollListener: (Event) -> Unit = {
        onScroll(IEVPPageState.SectionMeasures.fromElements(element, scrollingArea, 0.3f))
    }

    scrollingArea.addEventListener("scroll", scrollListener)
    observer.observe(element)
    onDispose {
        scrollingArea.removeEventListener("scroll", scrollListener)
        observer.disconnect()
    }
}

@Composable
private fun CompanyForeword(foreword: Foreword) = when (foreword) {
    Foreword.POST_HOG -> PostHogForeword()
    Foreword.TRIGGER_DEV -> TriggerDevForeword()
    Foreword.ICEYE -> ICEYEForeword()
    Foreword.BIOPTIMUS -> BioptimusForeword()
    Foreword.ZAPIER -> ZapierForeword()
    Foreword.MIX_RANK -> MixRankForeword()
    Foreword.TINY_PHARMA -> TinyPharmaForeword()
    Foreword.ATRIA -> AtriaForeword()
}


@Composable
private fun PartingWords() = Box(
    modifier = IEVPStylesheet.SupererogatorySection.toModifier(),
    contentAlignment = Alignment.Center,
) {
    Column(
        modifier = Modifier.fillMaxWidth().gap(SiteLayout.Space.XXLarge),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SpanText(
            "So that is me, out in the open, as honest as I can be. " +
                    "I hope it helps you make a decision - whatever it turns out to be. " +
                    "If you think transparency earns a seat at your table, let's talk.",
            modifier = IEVPStylesheet.SupererogatoryWords.toModifier(),
        )
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            MailLink(modifier = IEVPStylesheet.ContactMail.toModifier())
            SpanText("(Rotational, for spam protection)", modifier = Modifier.fontSize(FontTypography.Size.Small))
        }
        SpanText(
            "You can also ask my LLM assistant to summarize your findings:",
            modifier = IEVPStylesheet.SupererogatoryWords.toModifier(),
        )
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            SpanText("(no email gets stored)", modifier = Modifier.fontSize(FontTypography.Size.Small))
            SummaryMailInput()
        }
    }
}

private sealed interface MailSubmitResult {
    data object Success : MailSubmitResult
    data object InvalidEmail : MailSubmitResult
}

private fun String.isValidEmail(): Boolean =
    Regex("^[^@]+@[^@]+\\.[^@]+\$").matches(this.trim())

@OptIn(ExperimentalUuidApi::class, ExperimentalSerializationApi::class)
@Composable
private fun SummaryMailInput() {
    var email by remember { mutableStateOf("") }
    var result by remember { mutableStateOf<MailSubmitResult?>(null) }
    fun submit() {
        result = if (email.isValidEmail()) {
            val uuid = Uuid.random().toString()
            PostHogClient.capture(IEVPJourney.JourneySummaryRequested(uuid))
            val taskUrl = BuildConfig.ievpMailUrl ?: return
            window.fetch(
                taskUrl, RequestInit(
                    method = "POST",
                    headers = json("Content-Type" to "application/json"),
                    body = """{"email": "$email","journeyRandomId": "$uuid"}""",
                )
            )

            email = ""
            MailSubmitResult.Success
        } else {
            MailSubmitResult.InvalidEmail
        }
    }

    Column(
        modifier = Modifier.fillMaxWidth().gap(SiteLayout.Space.XXSmall)
            .thenIf(result is MailSubmitResult.InvalidEmail) {
                Modifier
                    .setVariable(IEVPStylesheet.SummaryMailInput.Vars.borderColor, Colors.Red)
                    .setVariable(IEVPStylesheet.SummaryMailInput.Vars.feedbackColor, Colors.Red)
            },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Row(
            modifier = IEVPStylesheet.SummaryMailInput.Container.toModifier(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            TextInput(
                text = email,
                onTextChange = { email = it; result = null },
                placeholder = "your@email.com",
                modifier = IEVPStylesheet.SummaryMailInput.Field.toModifier().weight(1f),
                onCommit = { submit() },
            )
            IconButton(
                onClick = { submit() },
                variant = ButtonStylesheet.GhostVariant,
            ) { FaPaperPlane() }
        }
        result?.let { r ->
            SpanText(
                text = when (r) {
                    MailSubmitResult.Success -> "Workin' on it, chief! Give me... 3 minutes?"
                    MailSubmitResult.InvalidEmail -> "the email seems invalid"
                },
                modifier = IEVPStylesheet.SummaryMailInput.Feedback.toModifier(),
            )
        }
    }
}
